<script setup lang="ts">
// NPM
import { computed, toRef } from 'vue';
import { useI18n } from 'vue-i18n';

// Types
import type { PropType } from 'vue';
import type {
  ManageRewardsItem,
  ManageRewardsRole,
} from './manage-rewards/types';

// UI Components
import ManageRewardsModalShell from './manage-rewards/ManageRewardsModalShell.vue';
import ManageRewardsRewardView from './manage-rewards/ManageRewardsRewardView.vue';
import ManageRewardsPurchaseView from './manage-rewards/ManageRewardsPurchaseView.vue';
import ManageRewardsPurchaseSuccessView from './manage-rewards/ManageRewardsPurchaseSuccessView.vue';
import ManageRewardsPurchasedView from './manage-rewards/ManageRewardsPurchasedView.vue';

// Composables
import { useManageRewardsFlow } from './manage-rewards/composables/useManageRewardsFlow';

// Stores
import { useCommonStore } from '@/stores/common';
import { useRewardsStore } from '@/stores/rewards';
import { useUserStore } from '@/stores/user';
const commonStore = useCommonStore();
const rewardsStore = useRewardsStore();
const userStore = useUserStore();

// Plugins
const { t } = useI18n({ useScope: 'global' });

// Props
const props = defineProps({
  description: {
    type: String,
    default: null,
  },
  closeCta: {
    type: String,
    default: null,
  },
  open: {
    type: Boolean,
    default: false,
  },
  item: {
    type: Object as PropType<ManageRewardsItem>,
    default: null,
  },
  role: {
    type: String as PropType<ManageRewardsRole>,
    default: null,
  },
  title: {
    type: String,
    default: null,
  },
});

// Flow wiring
const roleRef = toRef(props, 'role');
const {
  isSaving,
  onCloseModal,
  onContactAdmin,
  onFinalizePurchaseReward,
  onPurchaseReward,
} = useManageRewardsFlow({
  role: roleRef,
  commonStore,
  rewardsStore,
  userStore,
  t,
});

// Derived state
const item = computed(() => props.item ?? null);
const balance = computed(() => userStore.user?.balance);
const pointEnabled = computed(() => userStore.user?.organization?.pointEnabled);
const contactEmail = computed(
  () => userStore.user?.organization?.notificationEmail ?? null
);

const showBuyButton = computed(() => !!balance.value);
const buyDisabled = computed(() => {
  if (!item.value || !balance.value) {
    return true;
  }
  return item.value.points > balance.value;
});

// Local handlers
const handleBuy = () => {
  if (item.value) {
    onPurchaseReward(item.value);
  }
};

const handleConfirmPurchase = () => {
  if (item.value) {
    onFinalizePurchaseReward(item.value);
  }
};
</script>

<template>
  <ManageRewardsModalShell :open="open" :role="role" @close="onCloseModal">
    <ManageRewardsPurchasedView
      v-if="role === 'reward-purchased' && item"
      :item="item"
      :contact-email="contactEmail"
      @close="onCloseModal"
      @contact-admin="onContactAdmin"
    />

    <ManageRewardsRewardView
      v-else-if="role === 'reward' && item"
      :item="item"
      :title="title"
      :description="description"
      :show-buy-button="showBuyButton"
      :buy-disabled="buyDisabled"
      @close="onCloseModal"
      @buy="handleBuy"
    />

    <ManageRewardsPurchaseView
      v-else-if="role === 'reward-purchase' && item"
      :item="item"
      :title="title"
      :description="description"
      :point-enabled="pointEnabled"
      :balance="balance ?? null"
      :is-saving="isSaving"
      @confirm="handleConfirmPurchase"
      @close="onCloseModal"
    />

    <ManageRewardsPurchaseSuccessView
      v-else-if="role === 'reward-purchase-successful'"
      :title="title"
      :description="description"
      :close-cta="closeCta"
      @close="onCloseModal"
    />
  </ManageRewardsModalShell>
</template>
